import * as Popover from "@radix-ui/react-popover";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  Database,
  Eye,
  ExternalLink,
  File,
  FileCode2,
  Folder,
  FolderTree,
  KeyRound,
  Package,
  PanelBottom,
  PanelRight,
  Sigma,
  Table2,
  X,
} from "lucide-react";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router";

import { SearchInput, useVirtualizer } from "nazare-ui/components";
import { JsonPreview } from "nazare-ui/components/preview";

import {
  type CatalogueResource,
  type DataColumn,
  type DataPage,
  type DataPageRequest,
  type DataSource,
  type RecordFilter,
  type S3Entry,
  useDataCatalogue,
  useEntityGraph,
  useInfiniteData,
  useS3Object,
} from "../../data-explorer-api";
import { usePageSearchSyncedToUrl } from "../../global-search-context";
import { LoadingBar } from "../../loading-bar";
import { useSecondaryHeader } from "../../secondary-header-context";
import { SkeletonBlock } from "../../skeleton-block";
import { useDocumentTitle } from "../../use-document-title";
import dynamoDbLogo from "./assets/amazon-dynamodb.svg";
import s3Logo from "./assets/amazon-s3.svg";
import postgresLogo from "./assets/postgresql.svg";
import {
  EntityMap,
  type EntityMapDrilldown,
  type EntityMapNode,
  type EntityMapVisualKind,
} from "./entity-map";
import styles from "./index.module.css";

const SOURCES: Array<{
  value: DataSource;
  label: string;
  shortLabel: string;
  logo: string;
}> = [
  { value: "postgres", label: "PostgreSQL", shortLabel: "PG", logo: postgresLogo },
  { value: "dynamodb", label: "DynamoDB", shortLabel: "DDB", logo: dynamoDbLogo },
  { value: "s3", label: "Amazon S3", shortLabel: "S3", logo: s3Logo },
];

const OPERATORS: Array<{ value: NonNullable<RecordFilter["op"]>; label: string }> = [
  { value: "contains", label: "Contains" },
  { value: "equals", label: "Equals" },
  { value: "starts", label: "Starts with" },
  { value: "gt", label: "Greater than" },
  { value: "lt", label: "Less than" },
  { value: "is-null", label: "Is null" },
];

const PAGE_SIZES = [25, 50, 100];
const LAYOUT_STORAGE_KEY = "nazare-data-explorer-layout";

const ENTITY_MAP_KINDS = new Set<EntityMapVisualKind>([
  "table",
  "view",
  "materialised-view",
  "function",
  "procedure",
  "dynamodb-table",
  "s3-bucket",
  "entity",
]);

function entityMapKind(value: string | null): EntityMapVisualKind | undefined {
  return value && ENTITY_MAP_KINDS.has(value as EntityMapVisualKind)
    ? (value as EntityMapVisualKind)
    : undefined;
}

type LayoutSettings = {
  catalogueWidth: number;
  previewRight: number;
  previewBottom: number;
  columnWidths: Record<string, Record<string, number>>;
};

const DEFAULT_LAYOUT: LayoutSettings = {
  catalogueWidth: 280,
  previewRight: 420,
  previewBottom: 300,
  columnWidths: {},
};

function readLayoutSettings(): LayoutSettings {
  if (typeof window === "undefined") return DEFAULT_LAYOUT;
  try {
    const stored = JSON.parse(window.localStorage.getItem(LAYOUT_STORAGE_KEY) ?? "{}") as
      Partial<LayoutSettings>;
    return {
      catalogueWidth: Number.isFinite(stored.catalogueWidth)
        ? Math.max(220, Math.min(480, stored.catalogueWidth!))
        : DEFAULT_LAYOUT.catalogueWidth,
      previewRight: Number.isFinite(stored.previewRight)
        ? Math.max(280, stored.previewRight!)
        : DEFAULT_LAYOUT.previewRight,
      previewBottom: Number.isFinite(stored.previewBottom)
        ? Math.max(220, stored.previewBottom!)
        : DEFAULT_LAYOUT.previewBottom,
      columnWidths:
        stored.columnWidths && typeof stored.columnWidths === "object"
          ? stored.columnWidths
          : DEFAULT_LAYOUT.columnWidths,
    };
  } catch {
    return DEFAULT_LAYOUT;
  }
}

type NumericLayoutSetting = "catalogueWidth" | "previewRight" | "previewBottom";

function saveLayoutSetting(key: NumericLayoutSetting, value: number) {
  try {
    const current = readLayoutSettings();
    window.localStorage.setItem(
      LAYOUT_STORAGE_KEY,
      JSON.stringify({ ...current, [key]: Math.round(value) }),
    );
  } catch {
    // Storage can be unavailable in privacy modes; resizing still works for this session.
  }
}

function saveColumnWidths(resourceKey: string, widths: Record<string, number>) {
  try {
    const current = readLayoutSettings();
    window.localStorage.setItem(
      LAYOUT_STORAGE_KEY,
      JSON.stringify({
        ...current,
        columnWidths: {
          ...current.columnWidths,
          [resourceKey]: Object.fromEntries(
            Object.entries(widths).map(([column, width]) => [column, Math.round(width)]),
          ),
        },
      }),
    );
  } catch {
    // Resizing remains available for this session when storage is unavailable.
  }
}

function usePersistedColumnWidths(
  resourceKey: string,
  defaults: Record<string, number>,
) {
  const [sizing, setSizing] = useState(() => ({
    resourceKey,
    widths: { ...defaults, ...readLayoutSettings().columnWidths[resourceKey] },
  }));

  useEffect(() => {
    setSizing((current) => ({
      resourceKey,
      widths: {
        ...defaults,
        ...(current.resourceKey === resourceKey
          ? current.widths
          : readLayoutSettings().columnWidths[resourceKey]),
      },
    }));
  }, [defaults, resourceKey]);

  useEffect(() => {
    if (sizing.resourceKey !== resourceKey) return;
    const timeout = window.setTimeout(
      () => saveColumnWidths(resourceKey, sizing.widths),
      150,
    );
    return () => window.clearTimeout(timeout);
  }, [resourceKey, sizing]);

  const widths = sizing.resourceKey === resourceKey
    ? sizing.widths
    : { ...defaults, ...readLayoutSettings().columnWidths[resourceKey] };
  const setColumnWidth = useCallback((column: string, width: number) => {
    setSizing((current) => ({
      resourceKey,
      widths: {
        ...defaults,
        ...(current.resourceKey === resourceKey ? current.widths : {}),
        [column]: Math.max(80, Math.min(720, width)),
      },
    }));
  }, [defaults, resourceKey]);

  return { widths, setColumnWidth };
}

function beginColumnResize(
  event: ReactPointerEvent<HTMLDivElement>,
  width: number,
  onResize: (width: number) => void,
) {
  event.preventDefault();
  event.stopPropagation();
  const startX = event.clientX;
  const move = (pointerEvent: PointerEvent) => {
    onResize(width + pointerEvent.clientX - startX);
  };
  const stop = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", stop);
    document.body.style.removeProperty("user-select");
    document.body.style.removeProperty("cursor");
  };
  document.body.style.userSelect = "none";
  document.body.style.cursor = "col-resize";
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop, { once: true });
}

type ParamPatch = Record<string, string | null | undefined>;

function applyParamPatch(current: URLSearchParams, patch: ParamPatch): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(patch)) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  return next;
}

function resourceHref(current: URLSearchParams, resource: CatalogueResource): string {
  const next = applyParamPatch(current, {
    resource: resource.id,
    cursor: null,
    object: null,
    prefix: null,
    field: null,
    op: null,
    value: null,
    sort: null,
    direction: null,
  });
  return `?${next}`;
}

function prefixHref(current: URLSearchParams, prefix: string): string {
  const next = applyParamPatch(current, {
    prefix,
    cursor: null,
    object: null,
  });
  return `?${next}`;
}

function S3PathBreadcrumb({
  bucket,
  prefix,
  objectKey,
  currentParams,
}: {
  bucket: string;
  prefix: string;
  objectKey: string | null;
  currentParams: URLSearchParams;
}) {
  const path = objectKey ?? prefix;
  const segments = path.split("/").filter(Boolean);
  const rootHref = `?${applyParamPatch(currentParams, {
    prefix: null,
    object: null,
  })}`;

  return (
    <nav className={styles.s3Breadcrumb} aria-label="S3 object path">
      <Folder size={14} aria-hidden="true" />
      {segments.length === 0 ? (
        <span className={styles.s3BreadcrumbCurrent} aria-current="page">{bucket}</span>
      ) : (
        <Link to={rootHref}>{bucket}</Link>
      )}
      {segments.map((segment, index) => {
        const isCurrent = index === segments.length - 1;
        const isObject = !!objectKey && isCurrent;
        const segmentPrefix = `${segments.slice(0, index + 1).join("/")}/`;
        const href = prefixHref(currentParams, segmentPrefix);
        return (
          <span key={`${segment}-${index}`} className={styles.s3BreadcrumbSegment}>
            <ChevronRight size={13} aria-hidden="true" />
            {isCurrent || isObject ? (
              <span className={styles.s3BreadcrumbCurrent} aria-current="page">{segment}</span>
            ) : (
              <Link to={href}>{segment}</Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value < 1_024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = value / 1_024;
  let unit = units[0];
  for (let index = 1; index < units.length && size >= 1_024; index += 1) {
    size /= 1_024;
    unit = units[index];
  }
  return `${size >= 10 ? size.toFixed(0) : size.toFixed(1)} ${unit}`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}

function compactValue(value: unknown): ReactNode {
  if (value === null || value === undefined) {
    return <span className={styles.nullValue}>null</span>;
  }
  if (typeof value === "boolean") {
    return <span className={value ? styles.trueValue : styles.falseValue}>{String(value)}</span>;
  }
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return (
    <span className={styles.cellValue} title={text}>
      {text}
    </span>
  );
}

function FilterMenu({
  label,
  active,
  children,
}: {
  label: string;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={active ? styles.filterButtonActive : styles.filterButton}
        >
          {label}
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className={styles.menuContent}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function MenuOption({
  selected,
  children,
  onClick,
}: {
  selected: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" className={styles.menuOption} onClick={onClick}>
      <span>{children}</span>
      {selected && <Check size={14} aria-hidden="true" />}
    </button>
  );
}

function ResourceMeta({ resource }: { resource: CatalogueResource }) {
  if (resource.metadata.selectable === false) {
    const args = resource.metadata.identityArguments;
    return (
      <span>
        {args ? `(${args})` : resource.kind}
        {resource.metadata.resultType ? ` → ${resource.metadata.resultType}` : ""}
      </span>
    );
  }
  if (resource.kind === "bucket") {
    return <span>{formatDate(resource.metadata.createdAt)}</span>;
  }
  if (resource.metadata.estimatedRows !== undefined) {
    return (
      <span>
        {resource.metadata.estimatedRows.toLocaleString("en-GB")} rows ·{" "}
        {formatBytes(resource.metadata.sizeBytes)}
      </span>
    );
  }
  return <span>{resource.kind}</span>;
}

function ResourceKindIcon({ kind, source }: { kind: string; source: DataSource }) {
  const normalised = kind.toLocaleLowerCase();
  if (source === "dynamodb") return <Database size={14} aria-hidden="true" />;
  if (normalised.includes("materialised") || normalised.includes("materialized")) {
    return <Package size={14} aria-hidden="true" />;
  }
  if (normalised.includes("view")) return <Eye size={14} aria-hidden="true" />;
  if (normalised.includes("function")) return <Sigma size={14} aria-hidden="true" />;
  if (normalised.includes("procedure")) return <FileCode2 size={14} aria-hidden="true" />;
  if (source === "s3" || normalised.includes("bucket")) return <Folder size={14} aria-hidden="true" />;
  return <Table2 size={14} aria-hidden="true" />;
}

function ResourceTreeLink({
  resource,
  selected,
  currentParams,
  source,
}: {
  resource: CatalogueResource;
  selected: boolean;
  currentParams: URLSearchParams;
  source: DataSource;
}) {
  return (
    <Link
      to={resourceHref(currentParams, resource)}
      className={selected ? styles.treeResourceActive : styles.treeResource}
    >
      <span className={styles.treeGuide} aria-hidden="true" />
      <span className={styles.treeResourceIcon}>
        <ResourceKindIcon kind={resource.kind} source={source} />
      </span>
      <span className={styles.resourceText}>
        <span className={styles.resourceName}>{resource.name}</span>
        <span className={styles.resourceMeta}>
          <ResourceMeta resource={resource} />
        </span>
      </span>
    </Link>
  );
}

function CatalogueSkeleton() {
  return (
    <div className={styles.treeSkeleton} aria-label="Loading catalogue">
      <div className={styles.treeSkeletonGroup}>
        <SkeletonBlock width={14} height={14} radius="3px" />
        <SkeletonBlock width={88} height={11} />
      </div>
      {Array.from({ length: 7 }, (_, index) => (
        <div key={index} className={styles.treeSkeletonResource} aria-hidden="true">
          <SkeletonBlock width={22} height={22} radius="var(--radius-xs)" />
          <span>
            <SkeletonBlock width={92 + (index % 3) * 18} height={11} />
            <SkeletonBlock width={62 + (index % 2) * 22} height={9} />
          </span>
        </div>
      ))}
    </div>
  );
}

function DataTableSkeleton() {
  const gridTemplateColumns = "46px repeat(5, minmax(180px, 1fr))";
  return (
    <div className={styles.tableScroll} aria-label="Loading data">
      <div
        className={styles.virtualHeader}
        style={{ gridTemplateColumns, minWidth: 946 }}
      >
        <div className={styles.rowNumberHead}>#</div>
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className={styles.virtualHeaderCell}>
            <SkeletonBlock width={64 + index * 9} height={10} />
          </div>
        ))}
      </div>
      <div className={styles.skeletonRows} style={{ minWidth: 946 }}>
        {Array.from({ length: 8 }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className={styles.skeletonDataRow}
            style={{ gridTemplateColumns }}
            aria-hidden="true"
          >
            <div className={styles.rowNumber}>
              <SkeletonBlock width={12} height={9} />
            </div>
            {Array.from({ length: 5 }, (_, columnIndex) => (
              <div key={columnIndex} className={styles.virtualCell}>
                <SkeletonBlock
                  width={72 + ((rowIndex + columnIndex) % 4) * 24}
                  height={10}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Catalogue({
  source,
  resources,
  selected,
  loading,
  currentParams,
}: {
  source: DataSource;
  resources: CatalogueResource[];
  selected: CatalogueResource | null;
  loading: boolean;
  currentParams: URLSearchParams;
}) {
  const sourceMeta = SOURCES.find((item) => item.value === source)!;
  const groups = useMemo(() => {
    if (source !== "postgres") {
      return [
        {
          id: source,
          label: source === "dynamodb" ? "Tables" : "Buckets",
          resources,
        },
      ];
    }
    const grouped = new Map<string, CatalogueResource[]>();
    for (const resource of resources) {
      const group = resource.namespace ?? "Other";
      grouped.set(group, [...(grouped.get(group) ?? []), resource]);
    }
    return Array.from(grouped, ([id, groupResources]) => ({
      id,
      label: id,
      resources: groupResources,
    }));
  }, [resources, source]);
  return (
    <aside className={styles.catalogue} aria-label={`${sourceMeta.label} catalogue`}>
      <div className={styles.catalogueHeader}>
        <span className={styles.catalogueTitle}>{sourceMeta.label}</span>
        {!loading && <span className={styles.catalogueCount}>{resources.length}</span>}
      </div>
      <div className={styles.resourceList}>
        {loading ? (
          <CatalogueSkeleton />
        ) : resources.length === 0 ? (
          <div className={styles.catalogueEmpty}>No resources match this view.</div>
        ) : (
          groups.map((group) => (
            <details
              key={group.id}
              className={styles.treeGroup}
              open={!selected || group.resources.some((resource) => resource.id === selected.id)}
            >
              <summary className={styles.treeGroupSummary}>
                <ChevronRight className={styles.treeChevron} size={14} aria-hidden="true" />
                <FolderTree size={14} aria-hidden="true" />
                <span>{group.label}</span>
                <small>{group.resources.length}</small>
              </summary>
              <div className={styles.treeChildren}>
                {group.resources.map((resource) => (
                  <ResourceTreeLink
                    key={resource.id}
                    resource={resource}
                    selected={selected?.id === resource.id}
                    currentParams={currentParams}
                    source={source}
                  />
                ))}
              </div>
            </details>
          ))
        )}
      </div>
    </aside>
  );
}

function ResourceSummary({
  source,
  resource,
  data,
}: {
  source: DataSource;
  resource: CatalogueResource;
  data: DataPage | undefined;
}) {
  const detail =
    source === "postgres"
      ? `${resource.namespace}.${resource.name}`
      : source === "dynamodb" && data?.source === "dynamodb"
        ? `${(data.metadata.itemCount ?? 0).toLocaleString("en-GB")} items · ${formatBytes(data.metadata.sizeBytes)}`
        : resource.name;
  return (
    <div className={styles.resourceSummary}>
      <div>
        <h1 className={styles.resourceTitle}>{resource.name}</h1>
        <p className={styles.resourceSubtitle}>{detail}</p>
      </div>
      <span className={styles.readOnlyBadge}>Read only</span>
    </div>
  );
}

function RoutineDetails({ resource }: { resource: CatalogueResource }) {
  const isProcedure = resource.kind.toLocaleLowerCase().includes("procedure");
  return (
    <section className={styles.routinePanel}>
      <span className={styles.routineIcon}>
        {isProcedure ? <FileCode2 size={22} aria-hidden="true" /> : <Sigma size={22} aria-hidden="true" />}
      </span>
      <div>
        <span className={styles.routineKind}>{resource.kind}</span>
        <code>
          {resource.name}({resource.metadata.identityArguments ?? ""})
        </code>
        {resource.metadata.resultType && (
          <p>Returns <strong>{resource.metadata.resultType}</strong></p>
        )}
      </div>
    </section>
  );
}

function RecordTable({
  rows,
  columns,
  sort,
  direction,
  currentParams,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  rows: Record<string, unknown>[];
  columns: DataColumn[];
  sort: string | null;
  direction: "asc" | "desc";
  currentParams: URLSearchParams;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const resourceKey = `records:${currentParams.get("resource") ?? "unknown"}`;
  const defaultColumnWidths = useMemo(
    () => Object.fromEntries(columns.map((column) => [column.name, 180])),
    [columns],
  );
  const { widths: columnWidths, setColumnWidth } = usePersistedColumnWidths(
    resourceKey,
    defaultColumnWidths,
  );
  const count = rows.length + (hasNextPage ? 1 : 0);
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 42,
    overscan: 12,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const lastVirtualIndex = virtualRows.at(-1)?.index ?? -1;

  useEffect(() => {
    if (
      lastVirtualIndex >= rows.length - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore();
    }
  }, [hasNextPage, isFetchingNextPage, lastVirtualIndex, onLoadMore, rows.length]);

  if (rows.length === 0) {
    return (
      <div className={styles.emptyData}>
        <Database size={22} aria-hidden="true" />
        <strong>No matching records</strong>
        <span>Adjust the global search or subnav filters.</span>
      </div>
    );
  }

  const gridTemplateColumns = `46px ${columns
    .map((column) => `${columnWidths[column.name] ?? 180}px`)
    .join(" ")}`;
  const gridWidth = 46 + columns.reduce(
    (total, column) => total + (columnWidths[column.name] ?? 180),
    0,
  );
  return (
    <div ref={scrollRef} className={styles.tableScroll} role="table" aria-rowcount={rows.length}>
      <div
        className={styles.virtualHeader}
        role="row"
        style={{ gridTemplateColumns, minWidth: gridWidth }}
      >
        <div className={styles.rowNumberHead} role="columnheader">#</div>
        {columns.map((column) => {
          const nextDirection = sort === column.name && direction === "asc" ? "desc" : "asc";
          const href = `?${applyParamPatch(currentParams, {
            sort: column.name,
            direction: nextDirection,
          })}`;
          return (
            <div key={column.name} className={styles.virtualHeaderCell} role="columnheader">
              <Link to={href} className={styles.columnHeaderLink}>
                <span>{column.name}</span>
                {column.primaryKey && <KeyRound size={11} aria-label="Primary key" />}
                {sort === column.name &&
                  (direction === "asc" ? (
                    <ArrowUp size={12} aria-hidden="true" />
                  ) : (
                    <ArrowDown size={12} aria-hidden="true" />
                  ))}
              </Link>
              {column.dataType && <span className={styles.columnType}>{column.dataType}</span>}
              <div
                className={styles.columnResizeHandle}
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize ${column.name} column`}
                onPointerDown={(event) =>
                  beginColumnResize(
                    event,
                    columnWidths[column.name] ?? 180,
                    (width) => setColumnWidth(column.name, width),
                  )
                }
                onDoubleClick={() => setColumnWidth(column.name, 180)}
              />
            </div>
          );
        })}
      </div>
      <div
        className={styles.virtualBody}
        style={{ height: virtualizer.getTotalSize(), minWidth: gridWidth }}
      >
        {virtualRows.map((virtualRow) => {
          const isLoader = virtualRow.index >= rows.length;
          return (
            <div
              key={virtualRow.key}
              className={isLoader ? styles.virtualLoaderRow : styles.virtualRow}
              role="row"
              style={{
                gridTemplateColumns,
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoader ? (
                <span>Loading more rows…</span>
              ) : (
                <>
                  <div className={styles.rowNumber} role="cell">{virtualRow.index + 1}</div>
                  {columns.map((column) => (
                    <div key={column.name} className={styles.virtualCell} role="cell">
                      {compactValue(rows[virtualRow.index]?.[column.name])}
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function S3Table({
  entries,
  bucket,
  currentParams,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  entries: S3Entry[];
  bucket: string;
  currentParams: URLSearchParams;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const s3Columns = useMemo(
    () => [
      { id: "name", label: "Name", width: 360 },
      { id: "type", label: "Type", width: 120 },
      { id: "lastModified", label: "Last modified", width: 190 },
      { id: "size", label: "Size", width: 100 },
      { id: "storageClass", label: "Storage class", width: 130 },
    ],
    [],
  );
  const defaultColumnWidths = useMemo(
    () => Object.fromEntries(s3Columns.map((column) => [column.id, column.width])),
    [s3Columns],
  );
  const { widths: columnWidths, setColumnWidth } = usePersistedColumnWidths(
    `s3:${bucket}`,
    defaultColumnWidths,
  );
  const count = entries.length + (hasNextPage ? 1 : 0);
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 42,
    overscan: 12,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const lastVirtualIndex = virtualRows.at(-1)?.index ?? -1;

  useEffect(() => {
    if (
      lastVirtualIndex >= entries.length - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore();
    }
  }, [entries.length, hasNextPage, isFetchingNextPage, lastVirtualIndex, onLoadMore]);

  if (entries.length === 0) {
    return (
      <div className={styles.emptyData}>
        <Cloud size={22} aria-hidden="true" />
        <strong>No matching objects</strong>
        <span>Adjust the global search or prefix filter.</span>
      </div>
    );
  }
  const gridTemplateColumns = s3Columns
    .map((column) => `${columnWidths[column.id] ?? column.width}px`)
    .join(" ");
  const gridWidth = s3Columns.reduce(
    (total, column) => total + (columnWidths[column.id] ?? column.width),
    0,
  );
  return (
    <div ref={scrollRef} className={styles.tableScroll} role="table" aria-rowcount={entries.length}>
      <div
        className={styles.virtualHeader}
        role="row"
        style={{ gridTemplateColumns, minWidth: gridWidth }}
      >
        {s3Columns.map((column) => (
          <div key={column.id} className={styles.virtualHeaderCell} role="columnheader">
            {column.label}
            <div
              className={styles.columnResizeHandle}
              role="separator"
              aria-orientation="vertical"
              aria-label={`Resize ${column.label} column`}
              onPointerDown={(event) =>
                beginColumnResize(
                  event,
                  columnWidths[column.id] ?? column.width,
                  (width) => setColumnWidth(column.id, width),
                )
              }
              onDoubleClick={() => setColumnWidth(column.id, column.width)}
            />
          </div>
        ))}
      </div>
      <div className={styles.virtualBody} style={{ height: virtualizer.getTotalSize(), minWidth: gridWidth }}>
        {virtualRows.map((virtualRow) => {
          const isLoader = virtualRow.index >= entries.length;
          const entry = entries[virtualRow.index];
          const link = entry?.kind === "prefix"
            ? prefixHref(currentParams, entry.key)
            : `?${applyParamPatch(currentParams, { object: entry?.key })}`;
          return (
            <div
              key={virtualRow.key}
              className={isLoader ? styles.virtualLoaderRow : styles.virtualRow}
              role="row"
              style={{
                gridTemplateColumns,
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoader || !entry ? (
                <span>Loading more objects…</span>
              ) : (
                <>
                  <div className={styles.virtualCell} role="cell">
                    <Link to={link} className={styles.keyLink}>
                      {entry.kind === "prefix" ? <Folder size={15} /> : <File size={15} />}
                      <span>{entry.key}</span>
                    </Link>
                  </div>
                  <div className={styles.virtualCell} role="cell">{entry.kind === "prefix" ? "Folder" : "Object"}</div>
                  <div className={styles.virtualCell} role="cell">{formatDate(entry.lastModified)}</div>
                  <div className={styles.virtualCell} role="cell">{entry.kind === "object" ? formatBytes(entry.size) : "—"}</div>
                  <div className={styles.virtualCell} role="cell">{entry.storageClass ?? "—"}</div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type PreviewPlacement = "right" | "bottom";

function PreviewPlacementControls({
  placement,
  onPlacementChange,
}: {
  placement: PreviewPlacement;
  onPlacementChange: (placement: PreviewPlacement) => void;
}) {
  return (
    <span className={styles.previewPlacementControls} aria-label="Preview position">
      <button
        type="button"
        className={placement === "right" ? styles.previewPlacementActive : undefined}
        onClick={() => onPlacementChange("right")}
        aria-label="Move preview to the right"
      >
        <PanelRight size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={placement === "bottom" ? styles.previewPlacementActive : undefined}
        onClick={() => onPlacementChange("bottom")}
        aria-label="Move preview to the bottom"
      >
        <PanelBottom size={14} aria-hidden="true" />
      </button>
    </span>
  );
}

function ResizablePreviewLayout({
  placement,
  data,
  preview,
}: {
  placement: PreviewPlacement;
  data: ReactNode;
  preview: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sizes, setSizes] = useState(() => {
    const settings = readLayoutSettings();
    return { right: settings.previewRight, bottom: settings.previewBottom };
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      saveLayoutSetting("previewRight", sizes.right);
      saveLayoutSetting("previewBottom", sizes.bottom);
    }, 150);
    return () => window.clearTimeout(timeout);
  }, [sizes.bottom, sizes.right]);

  const beginResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const bounds = container.getBoundingClientRect();
    const move = (pointerEvent: PointerEvent) => {
      if (placement === "right") {
        const next = Math.max(280, Math.min(bounds.width - 320, bounds.right - pointerEvent.clientX));
        setSizes((current) => ({ ...current, right: next }));
      } else {
        const next = Math.max(220, Math.min(bounds.height - 260, bounds.bottom - pointerEvent.clientY));
        setSizes((current) => ({ ...current, bottom: next }));
      }
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      document.body.style.removeProperty("user-select");
      document.body.style.removeProperty("cursor");
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = placement === "right" ? "col-resize" : "row-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop, { once: true });
  };

  return (
    <div
      ref={containerRef}
      className={
        placement === "right"
          ? styles.dataAndPreviewRight
          : styles.dataAndPreviewBottom
      }
      style={{ "--preview-size": `${sizes[placement]}px` } as CSSProperties}
    >
      {data}
      <div
        className={styles.previewResizeHandle}
        role="separator"
        aria-orientation={placement === "right" ? "vertical" : "horizontal"}
        aria-label="Resize file preview"
        onPointerDown={beginResize}
      />
      {preview}
    </div>
  );
}

function ObjectPreview({
  bucket,
  objectKey,
  placement,
  onPlacementChange,
}: {
  bucket: string;
  objectKey: string;
  placement: PreviewPlacement;
  onPlacementChange: (placement: PreviewPlacement) => void;
}) {
  const { data, isLoading, error } = useS3Object(bucket, objectKey);
  if (isLoading) {
    return (
      <section className={styles.previewPanel} aria-label="Object preview loading">
        <SkeletonBlock height={220} />
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className={styles.previewPanel}>
        <p className={styles.errorText}>{error?.message ?? "Object preview unavailable"}</p>
      </section>
    );
  }

  const isImage = data.contentType.startsWith("image/");
  const isVideo = data.contentType.startsWith("video/");
  const isPdf = data.contentType === "application/pdf";
  const isJson = data.contentType.includes("json") || /\.json$/i.test(data.key);
  return (
    <section className={styles.previewPanel} aria-label={`Preview ${data.key}`}>
      <div className={styles.previewHeader}>
        <div className={styles.previewHeading}>
          <File size={16} aria-hidden="true" />
          <span>
            <strong>{data.key}</strong>
            <small>
              {data.contentType} · {formatBytes(data.size)}
            </small>
          </span>
        </div>
        <span className={styles.previewActions}>
          <PreviewPlacementControls
            placement={placement}
            onPlacementChange={onPlacementChange}
          />
          <a href={data.url} target="_blank" rel="noreferrer" className={styles.openObjectLink}>
            Open object
            <ExternalLink size={13} aria-hidden="true" />
          </a>
        </span>
      </div>
      {data.text !== null ? (
        isJson ? (
          <div className={styles.jsonPreview}>
            <JsonPreview json={data.text} />
          </div>
        ) : (
          <pre className={styles.textPreview}>{data.text}</pre>
        )
      ) : isImage ? (
        <img src={data.url} alt={data.key} className={styles.imagePreview} />
      ) : isVideo ? (
        <video src={data.url} controls className={styles.videoPreview}>
          <track kind="captions" />
        </video>
      ) : isPdf ? (
        <iframe src={data.url} title={data.key} className={styles.documentPreview} />
      ) : (
        <div className={styles.binaryPreview}>
          <File size={28} aria-hidden="true" />
          <span>This object type opens in a separate browser tab.</span>
        </div>
      )}
      {data.truncated && (
        <p className={styles.previewNotice}>Preview limited to the first 256 KB.</p>
      )}
    </section>
  );
}

function DataExplorerView({
  source,
  isLanding = false,
}: {
  source: DataSource;
  isLanding?: boolean;
}) {
  useDocumentTitle("Data Explorer");
  const [searchParams, setSearchParams] = useSearchParams();
  const explorerRef = useRef<HTMLElement>(null);
  const [catalogueWidth, setCatalogueWidth] = useState(
    () => readLayoutSettings().catalogueWidth,
  );

  useEffect(() => {
    const timeout = window.setTimeout(
      () => saveLayoutSetting("catalogueWidth", catalogueWidth),
      150,
    );
    return () => window.clearTimeout(timeout);
  }, [catalogueWidth]);
  const query = usePageSearchSyncedToUrl("Search data…", "q");
  const deferredQuery = useDeferredValue(query.trim());
  const selectedId = searchParams.get("resource");
  const namespace = searchParams.get("schema");
  const field = searchParams.get("field");
  const op = searchParams.get("op") as RecordFilter["op"] | null;
  const value = searchParams.get("value") ?? "";
  const deferredValue = useDeferredValue(value);
  const prefix = searchParams.get("prefix") ?? "";
  const deferredPrefix = useDeferredValue(prefix);
  const sort = searchParams.get("sort");
  const direction = searchParams.get("direction") === "desc" ? "desc" : "asc";
  const requestedLimit = Number(searchParams.get("limit"));
  const limit = PAGE_SIZES.includes(requestedLimit) ? requestedLimit : 50;
  const objectKey = searchParams.get("object");
  const previewPlacement: PreviewPlacement =
    searchParams.get("preview") === "right" ? "right" : "bottom";
  const mapSchema = isLanding ? searchParams.get("schema") : null;
  const mapKind = isLanding ? entityMapKind(searchParams.get("kind")) : undefined;
  const mapBucket = isLanding && mapKind ? searchParams.get("bucket") ?? undefined : undefined;
  const mapDrilldown = useMemo<EntityMapDrilldown | null>(
    () =>
      mapSchema
        ? {
            schema: mapSchema,
            ...(mapKind ? { kind: mapKind } : {}),
            ...(mapBucket ? { bucket: mapBucket } : {}),
          }
        : null,
    [mapBucket, mapKind, mapSchema],
  );
  const mapSelectedNodeId = isLanding ? searchParams.get("entity") : null;

  const catalogueQuery = useDataCatalogue(source, !isLanding);
  const allResources = catalogueQuery.data ?? [];
  const namespaces = useMemo(
    () =>
      Array.from(
        new Set(allResources.map((resource) => resource.namespace).filter(Boolean)),
      ).sort() as string[],
    [allResources],
  );
  const selectedResource =
    allResources.find((resource) => resource.id === selectedId) ?? null;
  const graphQuery = useEntityGraph(isLanding);
  const visibleGraph = useMemo(() => {
    const graph = graphQuery.data;
    if (!graph || !deferredQuery) return graph;
    const needle = deferredQuery.toLocaleLowerCase();
    const directIds = new Set(
      graph.nodes
        .filter((node) =>
          `${node.schema ?? ""} ${node.label} ${node.kind} ${node.source}`
            .toLocaleLowerCase()
            .includes(needle),
        )
        .map((node) => node.id),
    );
    const edges = graph.edges.filter(
      (edge) => directIds.has(edge.source) || directIds.has(edge.target),
    );
    const contextualIds = new Set(directIds);
    for (const edge of edges) {
      contextualIds.add(edge.source);
      contextualIds.add(edge.target);
    }
    return {
      nodes: graph.nodes.filter((node) => contextualIds.has(node.id)),
      edges,
    };
  }, [deferredQuery, graphQuery.data]);
  const visibleResources = useMemo(() => {
    const inNamespace = namespace
      ? allResources.filter((resource) => resource.namespace === namespace)
      : allResources;
    if (selectedResource || !deferredQuery) return inNamespace;
    const lowered = deferredQuery.toLocaleLowerCase();
    return inNamespace.filter((resource) =>
      `${resource.namespace ?? ""}.${resource.name}`.toLocaleLowerCase().includes(lowered),
    );
  }, [allResources, deferredQuery, namespace, selectedResource]);

  const request: DataPageRequest | null =
    selectedResource && selectedResource.metadata.selectable !== false
    ? {
        source,
        resource: selectedResource,
        q: deferredQuery,
        field,
        op,
        value: deferredValue,
        prefix: deferredPrefix,
        sort,
        direction,
        limit,
      }
    : null;
  const dataQuery = useInfiniteData(request);
  const pages = dataQuery.data?.pages ?? [];
  const data = pages[0];
  const rows = useMemo(
    () => pages.flatMap((page) => (page.source === "s3" ? [] : page.rows)),
    [pages],
  );
  const entries = useMemo(
    () => pages.flatMap((page) => (page.source === "s3" ? page.entries : [])),
    [pages],
  );
  const columns: DataColumn[] = useMemo(() => {
    if (!data || data.source === "s3") return [];
    if (data.source === "dynamodb") {
      const names = Array.from(
        new Set(
          pages.flatMap((page) => (page.source === "dynamodb" ? page.columns : [])),
        ),
      ).sort();
      return names.map((name) => ({
        name,
        primaryKey: data.metadata.keySchema.some((key) => key.AttributeName === name),
      }));
    }
    return data.columns.map((column) => ({
      name: column.name,
      dataType: column.data_type,
      nullable: column.nullable,
      primaryKey: column.primary_key,
    }));
  }, [data, pages]);

  const loadMore = useCallback(() => {
    void dataQuery.fetchNextPage();
  }, [dataQuery.fetchNextPage]);

  const mapNodeHref = useCallback(
    (node: EntityMapNode) => {
      const next = applyParamPatch(searchParams, {
        source: null,
        resource: null,
        schema: null,
        kind: null,
        bucket: null,
        entity: null,
        field: null,
        op: null,
        value: null,
        prefix: null,
        cursor: null,
        object: null,
        sort: null,
        direction: null,
      });
      if (node.metadata?.aggregate === true) {
        if (node.source === "postgres" && (node.schema ?? node.namespace)) {
          next.set("schema", node.schema ?? node.namespace!);
        }
        return `/data-explorer/${node.source}?${next}`;
      }
      const name = node.name ?? node.label;
      const isRoutine =
        node.source === "postgres" &&
        (node.kind.toLocaleLowerCase().includes("function") ||
          node.kind.toLocaleLowerCase().includes("procedure"));
      const identityArguments = node.metadata?.identityArguments;
      const resourceId =
        node.source === "postgres"
          ? `${node.schema ?? node.namespace ?? "public"}.${name}${
              isRoutine ? `(${typeof identityArguments === "string" ? identityArguments : ""})` : ""
            }`
          : name;
      next.set("resource", resourceId);
      return `/data-explorer/${node.source}?${next}`;
    },
    [searchParams],
  );

  const updateMapDrilldown = useCallback(
    (nextDrilldown: EntityMapDrilldown | null, options?: { replace?: boolean }) => {
      setSearchParams(
        (current) =>
          applyParamPatch(current, {
            schema: nextDrilldown?.schema,
            kind: nextDrilldown?.kind,
            bucket: nextDrilldown?.bucket,
            entity: null,
          }),
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const updateMapSelection = useCallback(
    (node: EntityMapNode | null) => {
      setSearchParams(
        (current) => applyParamPatch(current, { entity: node?.id }),
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const beginCatalogueResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const container = explorerRef.current;
    if (!container) return;
    const bounds = container.getBoundingClientRect();
    const move = (pointerEvent: PointerEvent) => {
      setCatalogueWidth(
        Math.max(220, Math.min(480, pointerEvent.clientX - bounds.left)),
      );
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      document.body.style.removeProperty("user-select");
      document.body.style.removeProperty("cursor");
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop, { once: true });
  };

  const updateParams = useCallback(
    (patch: ParamPatch) => {
      setSearchParams(
        (current) => applyParamPatch(current, { ...patch, cursor: null }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(
    () =>
      updateParams({
        schema: null,
        field: null,
        op: null,
        value: null,
        sort: null,
        direction: null,
      }),
    [updateParams],
  );

  const activeFilterCount = [namespace, field, op, value, sort].filter(Boolean).length;
  const secondaryHeader = useMemo(
    () => isLanding ? null : (
      <div className={styles.secondaryHeader}>
        {source === "postgres" && (
          <FilterMenu label={namespace ?? "All schemas"} active={!!namespace}>
            <MenuOption selected={!namespace} onClick={() => updateParams({ schema: null })}>
              All schemas
            </MenuOption>
            {namespaces.map((item) => (
              <MenuOption
                key={item}
                selected={namespace === item}
                onClick={() => updateParams({ schema: item, resource: null })}
              >
                {item}
              </MenuOption>
            ))}
          </FilterMenu>
        )}
        {source !== "s3" && selectedResource && columns.length > 0 && (
          <>
            <FilterMenu label={field ?? "Any field"} active={!!field}>
              <MenuOption
                selected={!field}
                onClick={() => updateParams({ field: null, op: null, value: null })}
              >
                Any field
              </MenuOption>
              {columns.map((column) => (
                <MenuOption
                  key={column.name}
                  selected={field === column.name}
                  onClick={() =>
                    updateParams({
                      field: column.name,
                      op: op ?? "contains",
                    })
                  }
                >
                  {column.name}
                </MenuOption>
              ))}
            </FilterMenu>
            {field && (
              <FilterMenu
                label={OPERATORS.find((operator) => operator.value === op)?.label ?? "Contains"}
                active
              >
                {OPERATORS.map((operator) => (
                  <MenuOption
                    key={operator.value}
                    selected={op === operator.value}
                    onClick={() => updateParams({ op: operator.value })}
                  >
                    {operator.label}
                  </MenuOption>
                ))}
              </FilterMenu>
            )}
            {field && op !== "is-null" && (
              <SearchInput
                type="text"
                value={value}
                onChange={(event) => updateParams({ value: event.target.value })}
                placeholder="Filter value"
                aria-label="Filter value"
                wrapperClassName={styles.filterValueWrapper}
                className={styles.filterValueInput}
              />
            )}
          </>
        )}
        <FilterMenu label={`${limit} rows`} active={limit !== 50}>
          {PAGE_SIZES.map((size) => (
            <MenuOption
              key={size}
              selected={limit === size}
              onClick={() => updateParams({ limit: String(size) })}
            >
              {size} rows
            </MenuOption>
          ))}
        </FilterMenu>
        {activeFilterCount > 0 && (
          <button type="button" className={styles.clearButton} onClick={clearFilters}>
            <X size={13} aria-hidden="true" />
            Clear {activeFilterCount}
          </button>
        )}
        <span className={styles.headerSpacer} />
      </div>
    ),
    [
      activeFilterCount,
      clearFilters,
      columns,
      field,
      isLanding,
      limit,
      namespace,
      namespaces,
      op,
      selectedResource,
      source,
      updateParams,
      value,
    ],
  );
  useSecondaryHeader(secondaryHeader);

  const isRefreshing =
    dataQuery.isFetching && !dataQuery.isFetchingNextPage && !dataQuery.isLoading;
  const loadedMetric = useMemo(() => {
    if (!data) return "";
    if (data.source === "dynamodb") {
      const scanned = pages.reduce(
        (total, page) => total + (page.source === "dynamodb" ? page.scanned : 0),
        0,
      );
      return `${rows.length.toLocaleString("en-GB")} rows · ${scanned.toLocaleString("en-GB")} scanned`;
    }
    if (data.source === "s3") {
      const inspected = pages.reduce(
        (total, page) => total + (page.source === "s3" ? page.inspected : 0),
        0,
      );
      return `${entries.length.toLocaleString("en-GB")} entries · ${inspected.toLocaleString("en-GB")} inspected`;
    }
    return `${rows.length.toLocaleString("en-GB")} rows`;
  }, [data, entries.length, pages, rows.length]);

  const dataCard = selectedResource ? (
    <section className={styles.dataCard}>
      {isRefreshing && <LoadingBar />}
      {source === "s3" && (
        <S3PathBreadcrumb
          bucket={selectedResource.name}
          prefix={prefix}
          objectKey={objectKey}
          currentParams={searchParams}
        />
      )}
      {dataQuery.isLoading ? (
        <DataTableSkeleton />
      ) : dataQuery.error ? (
        <div className={styles.errorStateCompact}>
          <strong>Could not read this resource</strong>
          <span>{dataQuery.error.message}</span>
        </div>
      ) : data?.source === "s3" ? (
        <S3Table
          entries={entries}
          bucket={selectedResource.name}
          currentParams={searchParams}
          hasNextPage={!!dataQuery.hasNextPage}
          isFetchingNextPage={dataQuery.isFetchingNextPage}
          onLoadMore={loadMore}
        />
      ) : data ? (
        <RecordTable
          rows={rows}
          columns={columns}
          sort={sort}
          direction={direction}
          currentParams={searchParams}
          hasNextPage={!!dataQuery.hasNextPage}
          isFetchingNextPage={dataQuery.isFetchingNextPage}
          onLoadMore={loadMore}
        />
      ) : null}
      {data && !dataQuery.error && (
        <footer className={styles.pagination}>
          <span>{loadedMetric}</span>
          <span>
            {dataQuery.isFetchingNextPage
              ? "Loading more…"
              : dataQuery.hasNextPage
                ? "Scroll for more"
                : "End of results"}
          </span>
        </footer>
      )}
    </section>
  ) : null;

  return (
    <div className={styles.page}>
      <section
        ref={explorerRef}
        className={isLanding ? styles.explorerCardLanding : styles.explorerCard}
        style={{ "--catalogue-width": `${catalogueWidth}px` } as CSSProperties}
      >
        {!isLanding && (
          <>
            <Catalogue
              source={source}
              resources={visibleResources}
              selected={selectedResource}
              loading={catalogueQuery.isLoading}
              currentParams={searchParams}
            />
            <div
              className={styles.catalogueResizeHandle}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize catalogue panel"
              onPointerDown={beginCatalogueResize}
            />
          </>
        )}
        <main className={styles.workspace}>
        {isLanding ? (
          <div className={styles.landingContent}>
            <nav className={styles.storeCards} aria-label="Data stores">
              {SOURCES.map((store) => {
                const entityCount = graphQuery.data?.nodes.filter(
                  (node) => node.source === store.value,
                ).length;
                return (
                  <Link
                    key={store.value}
                    to={`/data-explorer/${store.value}`}
                    className={styles.storeCard}
                  >
                    <span className={styles.storeCardLogo}>
                      <img src={store.logo} alt="" />
                    </span>
                    <span className={styles.storeCardText}>
                      <strong>{store.label}</strong>
                      <small>
                        {entityCount === undefined
                          ? "Loading catalogue…"
                          : `${entityCount.toLocaleString("en-GB")} entities`}
                      </small>
                    </span>
                    <ChevronRight size={16} aria-hidden="true" />
                  </Link>
                );
              })}
            </nav>
            <section className={styles.mapLanding} aria-label="Data entity relationships">
            <div className={styles.mapHeading}>
              <div>
                <h1>Entity relationships</h1>
                <p>Every PostgreSQL, DynamoDB, and S3 entity, linked by keys and constraints.</p>
              </div>
              {visibleGraph && (
                <span>{visibleGraph.nodes.length} entities · {visibleGraph.edges.length} links</span>
              )}
            </div>
            {graphQuery.isLoading ? (
              <div className={styles.mapSkeleton} aria-label="Loading entity map">
                {Array.from({ length: 12 }, (_, index) => (
                  <span
                    key={index}
                    style={{
                      left: `${10 + ((index * 29) % 78)}%`,
                      top: `${14 + ((index * 37) % 66)}%`,
                    }}
                  >
                    <SkeletonBlock width={26} height={26} radius="var(--radius-xs)" />
                    <SkeletonBlock width={68 + (index % 3) * 18} height={10} />
                  </span>
                ))}
              </div>
            ) : graphQuery.error ? (
              <div className={styles.errorStateCompact}>
                <strong>Entity map unavailable</strong>
                <span>{graphQuery.error.message}</span>
              </div>
            ) : (
              <EntityMap
                nodes={visibleGraph?.nodes ?? []}
                edges={visibleGraph?.edges ?? []}
                drilldown={mapDrilldown}
                onDrilldownChange={updateMapDrilldown}
                selectedNodeId={mapSelectedNodeId}
                onSelectNode={updateMapSelection}
                emptyMessage={deferredQuery ? "No entities match this search" : undefined}
                getNodeHref={mapNodeHref}
              />
            )}
            </section>
          </div>
        ) : catalogueQuery.error ? (
          <div className={styles.errorState}>
            <Database size={24} aria-hidden="true" />
            <strong>Catalogue unavailable</strong>
            <span>{catalogueQuery.error.message}</span>
          </div>
        ) : !selectedResource ? (
          <div className={styles.welcomeState}>
            <strong>Select a resource</strong>
            <span>Choose an item from the tree to inspect its data.</span>
          </div>
        ) : (
          <>
            <ResourceSummary source={source} resource={selectedResource} data={data} />
            {selectedResource.metadata.selectable === false ? (
              <RoutineDetails resource={selectedResource} />
            ) : source === "s3" && objectKey ? (
              <ResizablePreviewLayout
                placement={previewPlacement}
                data={dataCard}
                preview={
                  <ObjectPreview
                    bucket={selectedResource.name}
                    objectKey={objectKey}
                    placement={previewPlacement}
                    onPlacementChange={(placement) => updateParams({ preview: placement })}
                  />
                }
              />
            ) : (
              dataCard
            )}
          </>
        )}
        </main>
      </section>
    </div>
  );
}

export function DataExplorerLandingPage() {
  return <DataExplorerView source="postgres" isLanding />;
}

export function PostgresDataExplorerPage() {
  return <DataExplorerView source="postgres" />;
}

export function DynamoDataExplorerPage() {
  return <DataExplorerView source="dynamodb" />;
}

export function S3DataExplorerPage() {
  return <DataExplorerView source="s3" />;
}
